import { FC, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button, Label, Select, Textarea, TextInput, Spinner, Alert, ToggleSwitch } from "flowbite-react";
import { HiInformationCircle, HiPlus, HiSparkles } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import {
  useGetAIAgentQuery,
  useUpdateAIAgentMutation,
  useCreateAIAgentMutation,
  AIAgentUpdatePayload,
  AIAgentCreatePayload,
} from "@/api/companyApi/aiAgent";
import { useGetCitiesQuery } from "@/api/locationApi/city";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isAdminTierUser } from "@/constants/agent";
import { LLM_OPTIONS } from "@/constants/aiAgent";
import AIAgentCreateDialog from "@/components/companySettings/AIAgentCreateDialog";
import { getErrorMessage } from "@/utils/requestUtils";

// Real IANA zone names, generated client-side — same source Node's
// Intl.supportedValuesOf uses, no backend round-trip needed since this list
// never varies by tenant. Falls back to an empty list on very old browsers
// that lack the API (the field just won't offer suggestions there).
const TIMEZONE_OPTIONS: { label: string; value: string }[] =
  typeof Intl !== "undefined" && typeof (Intl as any).supportedValuesOf === "function"
    ? ((Intl as any).supportedValuesOf("timeZone") as string[]).map((tz) => ({ label: tz, value: tz }))
    : [];

const AIAgentConfigTab: FC = () => {
  const { t } = useTranslation(["companySetting", "common", "temporalActivity"]);
  const currentUser = useCurrentUser();
  // Frontline / Backoffice / Other can VIEW the AI agent config but cannot
  // mutate it. Only management / company_admin / super_admin may save,
  // sync KB, or toggle the agent on/off.
  const canEdit = isAdminTierUser(currentUser);
  const { data: agent, isLoading, isError, refetch } = useGetAIAgentQuery();

  const [updateAgent, { isLoading: isSaving }] = useUpdateAIAgentMutation();
  const [createAgent, { isLoading: isCreating, error: createError }] =
    useCreateAIAgentMutation();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<AIAgentUpdatePayload>({
    defaultValues: {
      display_name: "",
      company_scope: "",
      ai_greeting_text: "",
      chosen_llm: "gpt-4",
      events_enabled: false,
      events_default_city: "",
      timezone: "",
    },
  });

  useEffect(() => {
    if (agent) {
      reset({
        display_name: agent.display_name ?? "",
        company_scope: agent.company_scope ?? "",
        ai_greeting_text: agent.ai_greeting_text ?? "",
        chosen_llm: agent.chosen_llm ?? "gpt-4",
        events_enabled: agent.events_enabled ?? false,
        events_default_city: agent.events_default_city ?? "",
        timezone: agent.timezone ?? "",
      });
    }
  }, [agent, reset]);

  const [cityPage, setCityPage] = useState(1);
  const [citySearch, setCitySearch] = useState("");
  const { data: citiesData, isFetching: isCitiesFetching } = useGetCitiesQuery({
    pagination: { page: cityPage, pageSize: 15 },
    filters: citySearch ? { "city_name[like]": citySearch } : {},
  });
  const cityOptions = (citiesData?.items || []).map((c: any) => ({ label: c.city_name, value: c.city_name }));

  const [timezoneSearch, setTimezoneSearch] = useState("");
  const filteredTimezoneOptions = useMemo(() => {
    if (!timezoneSearch) return TIMEZONE_OPTIONS.slice(0, 100);
    const normalized = timezoneSearch.toLowerCase();
    return TIMEZONE_OPTIONS.filter((o) => o.label.toLowerCase().includes(normalized)).slice(0, 100);
  }, [timezoneSearch]);

  const onCreate = async (values: AIAgentCreatePayload) => {
    try {
      await createAgent(values).unwrap();
      toast.success(t("companySetting:aiAgent.create.success"));
      setShowCreateDialog(false);
      refetch();
    } catch (err: any) {
      toast.error(getErrorMessage(err) || t("companySetting:aiAgent.create.error"));
    }
  };

  const onSubmit = async (values: AIAgentUpdatePayload) => {
    try {
      await updateAgent(values).unwrap();
      toast.success(t("companySetting:saveSuccess"));
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.error || t("companySetting:saveError"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert color="failure" className="mt-4">
        {t("companySetting:loadError")}
      </Alert>
    );
  }

  // No agent yet: the company has to create one before there is anything to
  // configure.
  if (!agent) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t("companySetting:aiAgent.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("companySetting:aiAgent.description")}
          </p>
        </div>

        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center dark:border-gray-600 dark:bg-gray-800">
          <HiSparkles className="size-10 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            {t("companySetting:aiAgent.create.emptyTitle")}
          </h3>
          <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
            {canEdit
              ? t("companySetting:aiAgent.create.emptyDescription")
              : t("companySetting:aiAgent.create.emptyReadOnly")}
          </p>
          {canEdit && (
            <Button
              color="blue"
              className="mt-6"
              onClick={() => setShowCreateDialog(true)}
            >
              <HiPlus className="mr-2 size-4" />
              {t("companySetting:aiAgent.create.button")}
            </Button>
          )}
        </div>

        <AIAgentCreateDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSubmit={onCreate}
          isSubmitting={isCreating}
          errorMessage={getErrorMessage(createError)}
        />
      </div>
    );
  }

  const isActive = agent?.is_active !== false;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t("companySetting:aiAgent.title")}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t("companySetting:aiAgent.description")}
        </p>
      </div>

      {!canEdit && (
        <Alert color="info" icon={HiInformationCircle} className="mb-4">
          {t("companySetting:aiAgent.readOnlyNotice", {
            defaultValue:
              "You can view the AI Agent configuration. Only managers and company admins can change it.",
          })}
        </Alert>
      )}

      {/* AI Agent status panel */}
      <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t("companySetting:aiAgent.status", { defaultValue: "AI Agent Status" })}
          </h3>
        </div>

        <div className="flex items-center justify-between p-3 rounded-md bg-gray-50 dark:bg-gray-900/50">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {t("companySetting:training.agentStatus")}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {t(
              isActive
                ? "companySetting:training.active"
                : "companySetting:training.inactive"
            )}
          </span>
        </div>

        {!isActive && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
            <HiInformationCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p>{t("companySetting:training.inactiveNote")}</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Label htmlFor="ai_display_name" value={t("companySetting:aiAgent.create.nameLabel")} />
          <TextInput
            id="ai_display_name"
            placeholder={t("companySetting:aiAgent.create.namePlaceholder")}
            {...register("display_name", {
              required: t("companySetting:required") as string,
              validate: (value) => (value ?? "").trim().length > 0 || (t("companySetting:required") as string),
              maxLength: {
                value: 100,
                message: t("companySetting:aiAgent.create.nameMaxLength"),
              },
            })}
            color={errors.display_name ? "failure" : undefined}
            className="mt-1"
            disabled={!canEdit}
          />
          {errors.display_name && (
            <p className="text-sm text-red-600 mt-1">{errors.display_name.message}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.nameHelp")}
          </p>
        </div>

        <div>
          <Label htmlFor="chosen_llm" value={t("companySetting:aiAgent.llmLabel")} />
          <Controller
            name="chosen_llm"
            control={control}
            rules={{ required: t("companySetting:required") as string }}
            render={({ field }) => (
              <Select
                id="chosen_llm"
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
                color={errors.chosen_llm ? "failure" : undefined}
                className="mt-1"
                disabled
              >
                {/* llm selection disabled   not supported yet */}
                {LLM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.chosen_llm && (
            <p className="text-sm text-red-600 mt-1">{errors.chosen_llm.message}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.llmHelp")}
          </p>
        </div>

        <div>
          <Label htmlFor="ai_greeting_text" value={t("companySetting:aiAgent.greetingLabel")} />
          <Textarea
            id="ai_greeting_text"
            rows={3}
            placeholder={t("companySetting:aiAgent.greetingPlaceholder")}
            {...register("ai_greeting_text", {
              maxLength: {
                value: 500,
                message: t("companySetting:aiAgent.greetingMaxLength"),
              },
            })}
            color={errors.ai_greeting_text ? "failure" : undefined}
            className="mt-1"
            disabled={!canEdit}
          />
          {errors.ai_greeting_text && (
            <p className="text-sm text-red-600 mt-1">
              {errors.ai_greeting_text.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.greetingHelp")}
          </p>
        </div>

        <div>
          <Label htmlFor="company_scope" value={t("companySetting:aiAgent.scopeLabel")} />
          <Textarea
            id="company_scope"
            rows={8}
            placeholder={t("companySetting:aiAgent.scopePlaceholder")}
            {...register("company_scope", {
              required: t("companySetting:required") as string,
              maxLength: {
                value: 5000,
                message: t("companySetting:aiAgent.scopeMaxLength"),
              },
            })}
            color={errors.company_scope ? "failure" : undefined}
            className="mt-1"
            disabled={!canEdit}
          />
          {errors.company_scope && (
            <p className="text-sm text-red-600 mt-1">
              {errors.company_scope.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.scopeHelp")}
          </p>
        </div>

{/* Greeting Message gizlendi (comment out)
        <div>
          <Label
            htmlFor="ai_greeting_text"
            value={t("companySetting:aiAgent.greetingLabel")}
          />
          <Textarea
            id="ai_greeting_text"
            rows={3}
            placeholder={t("companySetting:aiAgent.greetingPlaceholder")}
            {...register("ai_greeting_text", {
              maxLength: {
                value: 1000,
                message: t("companySetting:aiAgent.greetingMaxLength"),
              },
            })}
            color={errors.ai_greeting_text ? "failure" : undefined}
            className="mt-1"
            disabled={!canEdit}
          />
          {errors.ai_greeting_text && (
            <p className="text-sm text-red-600 mt-1">
              {errors.ai_greeting_text.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:aiAgent.greetingHelp")}
          </p>
        </div>
*/}

        <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
          <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
            {t("temporalActivity:aiAgentSettings.title")}
          </h3>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            {t("temporalActivity:aiAgentSettings.description")}
          </p>

          <Controller
            name="events_enabled"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                checked={!!field.value}
                label={t("temporalActivity:aiAgentSettings.eventsEnabled")}
                onChange={field.onChange}
                disabled={!canEdit}
              />
            )}
          />

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="events_default_city"
              control={control}
              render={({ field }) => {
                const selected = field.value ? { label: field.value, value: field.value } : null;
                return (
                  <PaginatedSelect
                    id="events_default_city"
                    label={t("temporalActivity:aiAgentSettings.defaultCity")}
                    value={selected}
                    onChange={(val: any) => field.onChange(val?.value ?? "")}
                    options={cityOptions}
                    onInputChange={(val) => {
                      setCitySearch(val);
                      setCityPage(1);
                    }}
                    isLoading={isCitiesFetching}
                    isFetching={isCitiesFetching}
                    placeholder={t("temporalActivity:aiAgentSettings.defaultCityPlaceholder")}
                    page={cityPage}
                    totalPages={citiesData?.pagination?.totalPages || 1}
                    onPageChange={setCityPage}
                    isDisabled={!canEdit}
                  />
                );
              }}
            />

            <Controller
              name="timezone"
              control={control}
              render={({ field }) => {
                const selected = field.value ? { label: field.value, value: field.value } : null;
                return (
                  <PaginatedSelect
                    id="timezone"
                    label={t("temporalActivity:aiAgentSettings.timezone")}
                    value={selected}
                    onChange={(val: any) => field.onChange(val?.value ?? "")}
                    options={filteredTimezoneOptions}
                    onInputChange={setTimezoneSearch}
                    placeholder={t("temporalActivity:aiAgentSettings.timezonePlaceholder")}
                    page={1}
                    totalPages={1}
                    onPageChange={() => {}}
                    isDisabled={!canEdit}
                  />
                );
              }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t("temporalActivity:aiAgentSettings.defaultCityHelp")}
          </p>
        </div>

        {canEdit && (
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="submit" disabled={isSaving || !isDirty} color="blue">
              {isSaving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {t("companySetting:saving")}
                </>
              ) : (
                t("common:save")
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AIAgentConfigTab;
